import { Box, Button, Code, Flex, IconButton, Text, TextField } from "@radix-ui/themes";
import { Plus, Trash2 } from "lucide-react";

export interface PushButtonRow {
  id: string;
  label: string;
  click_action?: string;
}

interface Props {
  value: PushButtonRow[];
  onChange: (next: PushButtonRow[]) => void;
  hint?: React.ReactNode;
  // iOS renders max 4 action buttons; web Chrome only 2.
  maxButtons?: number;
  lockedByGroup?: string | null;
}

export default function PushButtonsField({
  value,
  onChange,
  hint,
  maxButtons = 4,
  lockedByGroup = null,
}: Props) {
  const locked = !!lockedByGroup;

  const update = (index: number, patch: Partial<PushButtonRow>) => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const remove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };
  const add = () => {
    if (value.length >= maxButtons) return;
    onChange([...value, { id: "", label: "", click_action: "" }]);
  };

  return (
    <Box>
      <Flex justify="between" align="end" mb="1">
        <Box>
          <Text as="label" size="2" weight="medium" style={{ display: "block" }}>
            Action buttons {locked ? "" : "(optional)"}
          </Text>
          {locked ? (
            <Box mt="1">
              <Text size="1" color="gray">
                Set by iOS button group <Code size="1" variant="ghost">{lockedByGroup}</Code>. The same id and label render on Android, Web, and iOS — only the tap target is editable per button.
              </Text>
            </Box>
          ) : hint ? (
            <Box mt="1">
              <Text size="1" color="gray">{hint}</Text>
            </Box>
          ) : null}
        </Box>
        {!locked && (
          <Text size="1" color="gray">{value.length} / {maxButtons}</Text>
        )}
      </Flex>

      {value.length === 0 ? (
        <Box>
          <Button type="button" variant="soft" onClick={add}>
            <Plus size={14} /> Add a button
          </Button>
        </Box>
      ) : locked ? (
        <Box style={{
          border: "1px solid var(--gray-a5)",
          borderRadius: "var(--radius-3)",
          overflow: "hidden",
        }}>
          {value.map((row, i) => (
            <Flex
              key={i}
              direction="column"
              gap="2"
              p="3"
              style={{
                borderTop: i > 0 ? "1px solid var(--gray-a4)" : "none",
                background: i % 2 === 0 ? "transparent" : "var(--gray-a2)",
              }}
            >
              <Flex align="center" gap="2">
                <Code size="1" variant="ghost">{row.id || "—"}</Code>
                <Text size="1" color="gray">→</Text>
                <Text size="2" weight="medium">{row.label || row.id || "—"}</Text>
              </Flex>
              <Box>
                <Text size="1" color="gray" mb="1" style={{ display: "block" }}>
                  Tap target (deep link / URL)
                </Text>
                <TextField.Root
                  placeholder="e.g. /screen/123 or https://example.com/route"
                  value={row.click_action ?? ""}
                  onChange={(e) => update(i, { click_action: e.target.value })}
                />
              </Box>
            </Flex>
          ))}
        </Box>
      ) : (
        <Flex direction="column" gap="2">
          {value.map((row, i) => (
            <Box
              key={i}
              p="2"
              style={{
                border: "1px solid var(--gray-5)",
                borderRadius: "var(--radius-2)",
              }}
            >
              <Flex gap="2" align="end" wrap="wrap">
                <Box style={{ flex: "1 1 140px" }}>
                  <Text size="1" weight="medium" mb="1" style={{ display: "block" }}>Action id</Text>
                  <TextField.Root
                    placeholder="reply"
                    value={row.id}
                    onChange={(e) => update(i, { id: e.target.value })}
                  />
                </Box>
                <Box style={{ flex: "1 1 160px" }}>
                  <Text size="1" weight="medium" mb="1" style={{ display: "block" }}>Label</Text>
                  <TextField.Root
                    placeholder="Reply"
                    value={row.label}
                    onChange={(e) => update(i, { label: e.target.value })}
                  />
                </Box>
                <Box style={{ flex: "2 1 220px" }}>
                  <Text size="1" weight="medium" mb="1" style={{ display: "block" }}>Tap target (deep link / URL)</Text>
                  <TextField.Root
                    placeholder="e.g. /screen/123 or https://example.com/route"
                    value={row.click_action ?? ""}
                    onChange={(e) => update(i, { click_action: e.target.value })}
                  />
                </Box>
                <IconButton
                  type="button"
                  variant="soft"
                  color="red"
                  onClick={() => remove(i)}
                  title="Remove button"
                >
                  <Trash2 size={14} />
                </IconButton>
              </Flex>
            </Box>
          ))}
          {value.length < maxButtons && (
            <Box>
              <Button type="button" variant="soft" onClick={add}>
                <Plus size={14} /> Add another button
              </Button>
            </Box>
          )}
        </Flex>
      )}

      {!locked && (
        <Box mt="2">
          <Text size="1" color="gray">
            On Android and Web the labels you type are used as-is. On iOS the labels are decided by the app code at launch —
            only the <strong>action ids</strong> need to match what the app registered for the chosen iOS button group.
          </Text>
        </Box>
      )}
    </Box>
  );
}
